import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { Pause, Play } from 'lucide-react'

import {
    motion,
    useAnimationFrame,
    useMotionValue,
    useScroll,
    useSpring,
    useTransform,
    useVelocity,
    wrap
} from 'motion/react'

import { cn } from '@/lib/utils'
import { useReduceMotion } from '@/hooks/use-reduce-motion'

interface MarqueeProps {
    children: ReactNode
    label: string
    speed?: number
    reactToScroll?: boolean
    className?: string
    trackClassName?: string
    controlClassName?: string
}

// An endless row that drifts on its own and speeds up (and leans) with the visitor's scrolling.
// It pauses on hover or keyboard focus, has its own pause button, and becomes a plain
// swipeable row for visitors who prefer reduced motion.
const Marquee: FC<MarqueeProps> = ({
    children,
    label,
    speed = 3,
    reactToScroll = true,
    className,
    trackClassName,
    controlClassName
}): ReactNode => {
    const reduce = useReduceMotion()
    const [paused, setPaused] = useState(false)
    const hovering = useRef(false)
    const direction = useRef(1)
    const baseX = useMotionValue(0)

    const { scrollY } = useScroll()
    const scrollVelocity = useVelocity(scrollY)
    const smoothVelocity = useSpring(scrollVelocity, { damping: 50, stiffness: 400 })
    const velocityFactor = useTransform(smoothVelocity, [0, 1000], [0, 1], { clamp: false })
    const skew = useTransform(smoothVelocity, [-2500, 2500], [7, -7])
    const x = useTransform(baseX, value => `${wrap(-50, 0, value)}%`)

    useAnimationFrame((_, delta) => {
        if (reduce || paused || hovering.current) return

        let moveBy = direction.current * -speed * (delta / 1000)

        if (reactToScroll) {
            const factor = velocityFactor.get()

            if (factor < 0) direction.current = -1
            else if (factor > 0) direction.current = 1

            moveBy += moveBy * Math.abs(factor)
        }

        baseX.set(baseX.get() + moveBy)
    })

    if (reduce) {
        return (
            <div className={cn('relative', className)}>
                <div tabIndex={0} role='region' aria-label={label} className='no-scrollbar overflow-x-auto'>
                    <div className={cn('flex w-max', trackClassName)}>
                        {children}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={cn('relative', className)}>
            <div
                className='overflow-hidden'
                onPointerEnter={() => {
                    hovering.current = true
                }}
                onPointerLeave={() => {
                    hovering.current = false
                }}
                onFocus={() => {
                    hovering.current = true
                }}
                onBlur={() => {
                    hovering.current = false
                }}
            >
                <motion.div
                    style={{ x, skewX: reactToScroll ? skew : 0 }}
                    className={cn('flex w-max', trackClassName)}
                >
                    <div className='flex shrink-0 items-stretch'>
                        {children}
                    </div>

                    <div aria-hidden='true' className='flex shrink-0 items-stretch' inert>
                        {children}
                    </div>
                </motion.div>
            </div>

            <button
                type='button'
                onClick={() => setPaused(value => !value)}
                className={cn(
                    'absolute right-0 top-1/2 grid size-9 -translate-y-1/2 place-items-center rounded-full border-2 border-current opacity-80 transition-opacity hover:opacity-100 focus-visible:opacity-100',
                    controlClassName
                )}
            >
                <span className='sr-only'>
                    {paused ? `Play ${label}` : `Pause ${label}`}
                </span>

                {paused ? <Play aria-hidden='true' className='size-3.5' /> : <Pause aria-hidden='true' className='size-3.5' />}
            </button>
        </div>
    )
}

export default Marquee
